<template>
	<div
		:id="uid"
		:class="[
			'integrations-card',
			source.hasErrorTransactions && 'has-error'
		]"
	>
		<div class="integrations-card__status">
			<NotificationIcon
				:variant="notificationVariantIcon"
				ref="tooltipStatusRef"
				@mouseenter="tooltipStatusVisible = true"
				@focus="tooltipStatusVisible = true"
				@mouseleave="tooltipStatusVisible = false"
				@blur="tooltipStatusVisible = false"
			/>
			<Tooltip
				:text="integrationStatus"
				:trackedRefName="'tooltipStatusRef'"
				:display="tooltipStatusVisible"
				:offset="[4, 4]"
				position="top-start"
			/>
		</div>

		<div class="integrations-card__main">
			<Ellipsis
				:data="source.name"
				class="integrations-card__title"
			></Ellipsis>

			<div class="integrations-card__data">
				<div class="integrations-card__data-alias" v-if="source.alias">
					{{ source.alias }}
				</div>

				<div class="integrations-card__data-int">
					<span class="integrations-card__data-int-type">
						{{ source.integrationType }}
						<span v-if="source.standardType">
							-
						</span>
						{{ source.standardType }}
					</span>

					<span
						class="integrations-card__data-int-separator"
						v-if="source.id"
						>|</span
					>

					<Ellipsis
						:data="'ID# ' + source.id"
						v-if="source.id"
					></Ellipsis>
				</div>

				<div class="integrations-card__data-solution">
					<TagLabel
						v-if="!source.agregatorIntegratorId"
						message="USES DMS-IS"
						:state="'secondary-2'"
						data-testid="displayIfDMSIS"
					/>
				</div>
			</div>
		</div>
		<ActionsSet
			class="integrations-card__actions"
			:id="`${uid}_action`"
			:variant="variant"
			:options="actions"
		/>
	</div>
</template>

<script>
import ActionsSet from '@/site-components/actions-set/actions-set';
import NotificationIcon from '@/components/notifications/notification-icon/notification-icon';
import Tooltip from '@/components/shells/tooltip/tooltip';
import Ellipsis from '@/components/visual/ellipsis';
import TagLabel from '@/components/visual/tag-label';

export default {
	name: 'IntegrationsSummaryCard',
	components: {
		ActionsSet,
		NotificationIcon,
		Tooltip,
		Ellipsis,
		TagLabel
	},
	props: {
		source: {
			type: Object,
			default: () => {}
		}
	},
	data() {
		return {
			tooltipStatusVisible: false,
			tooltipTitleVisible: false,
			tooltipCodeVisible: false,
			actions: [
				{
					id: 1,
					action: {
						name: 'vendors-integration-settings',
						params: {
							id: parseInt(this.source.id),
							integratorId: parseInt(
								this.source.agregatorIntegratorId
							),
							integratorName: this.source.integratorName
						}
					},
					title: !this.validatePermission('vendors.update')
						? " You don't have permission to perform this action."
						: 'Select to go to Integration Settings',
					iconClass: 'icon icon-document',
					label: 'Details',
					type: 'route-link',
					visible: true
				},
				{
					id: 2,
					action: () => console.log('CCL'),
					title:
						'Select to go to Integrations Client Companies Locations',
					iconClass: 'icon icon-history',
					label: 'CCL',
					valueBadgeCount: this.source.CCLCount,
					type: 'anchor',
					visible: true,
					disabled: true
				},
				{
					id: 3,
					action: () => this.$emit('displayApisModal', this.source),
					iconClass: 'icon icon-transaction',
					label: 'APIs',
					title: 'Select to go to Integration APIs',
					valueBadgeCount: this.source.apIsCount,
					type: 'anchor',
					visible: true
				},
				{
					id: 4,
					action: () => console.log('Transactions'),
					iconClass: 'icon icon-transaction',
					label: 'Transactions',
					title: 'Select to go to Integration Transactions',
					valueBadgeCount: 2,
					type: 'anchor',
					visible: true,
					disabled: true
				},
				{
					id: 5,
					action: '#',
					disabled: false,
					iconClass: 'icon icon-menu',
					label: 'More',
					title: 'select to see the more actions',
					type: 'dropdown',
					visible: true,
					items: [
						{
							id: 6,
							action: () => console.log('Copy Action'),
							iconClass: 'icon icon-copy',
							label: 'Create Copy',
							title: 'Select to make a Copy',
							type: 'anchor',
							disabled: !this.validatePermission(
								'vendors.update'
							),
							visible: true
						},
						{
							id: 7,
							action: {
								path: `/vendors/${this.source.agregatorIntegratorId}/integrations/history/${this.source.id}`
							},
							iconClass: 'icon icon-history',
							label: 'History',
							title: 'Select to go to the Integration History',
							type: 'route-link',
							visible: true,
							disabled: false
						}
					]
				}
			]
		};
	},
	methods: {
		mockFn() {
			console.log('go to integrations');
		}
	},
	computed: {
		integrationStatus() {
			if (this.source.hasErrorTransactions) {
				return 'Transaction Error';
			}
			if (!this.source.active) {
				return 'Inactive';
			}
			return 'Active';
		},
		notificationVariantIcon() {
			if (this.source.hasErrorTransactions) {
				return 'negative';
			}
			if (!this.source.active) {
				return 'inactive';
			}
			return 'positive';
		}
	}
};
</script>

<style lang="scss">
/**
	* 3P Vendor Integration Manager - Card
	* Can use SCSS variables = TRUE
*/

@import '@/scss/5-objects/_cards.scss';

.integrations-card {
	@include card;
	display: grid;
	grid-template-columns: auto 1fr;
	column-gap: $gutter-lg;
	row-gap: $gutter-md;
	align-items: start;
	font-size: $default-font-size - 2;

	&.has-error {
		@include card('error');
	}

	&__main {
		min-width: 0;
	}

	&__status {
		position: relative;
		@include vertical-horizontal-center-with-flex;
		z-index: 1;
		cursor: default;
	}

	&__title {
		cursor: default;
		@include heading('md');
		text-transform: capitalize;
		margin-bottom: $gutter-sm;
	}

	&__data {
		display: flex;
		flex-direction: column;
		gap: $gutter-sm;
		@include body-text('regular');
		margin-bottom: $gutter-md;
	}

	&__data-int {
		display: flex;
		flex-direction: row;
		flex-wrap: nowrap;
		gap: $gutter-sm;
	}

	&__data-int-type {
		min-width: fit-content;
	}

	&__data-int-separator {
		line-height: 1;
	}

	&__data-solution {
		display: flex;
		flex-wrap: wrap;
		gap: $gutter-md;
		margin-top: $gutter-lg;
	}

	&__actions {
		grid-column: 2/3;
		align-self: flex-end;
		display: flex;
		flex-direction: column;
	}
}
</style>
